<template>
  <div v-if="product">
    <StepTitle :session="session" hr>
      <div v-if="showStepLabels" v-html="session.label"></div>
      <div v-else>{{title}}</div>
      <template #subtitle>
        <div v-if="showStepLabels" v-html="session.subtitle"></div>
        <div v-else>{{subtitle}}</div>
      </template>
      <template #product>
        <table class="selected-item-table selected-item">
          <tr>
            <td><img :src="getImageUrl" alt=""></td>
            <td class="product-name">{{quantity}} &times; <span v-html="product.name"></span></td>
          </tr>
        </table>
      </template>
    </StepTitle>
    <template>
      <div>
        <section class="boxes select-item-table">
          <div class="index-content centered-block ">
            <b-row class="justify-content-md-center boxes select-item-btn customer-verify variant-selector-container">
              <b-col lg="12" cols="12">
                <VariantSelector
                  ref="variantSelector"
                  :variants="variantsList"
                  :quantity="quantity"
                  :bundle-quantity="bundleQuantity"
                  :initial-variant="initialVariant"
                  :initial-bundled-variants="initialBundledVariants"
                  @change="onVariantSelectionChange"
                ></VariantSelector>
              </b-col>
            </b-row>
          </div>
        </section>
      </div>
      <b-row v-if="showStepLabels" class="justify-content-md-center boxes claim mt-4 variant-btns-wrapper" :class="{'new-btns-container': !session.is_legacy}">
        <b-col md="7" cols="12" :class="{'my-class': !session.is_legacy}">
          <b-row class="choice-fields-btns">
            <b-col v-for="input in session.valid_inputs" :key="'choice-' + input.id" cols="12" class="text-center">
              <button @click="done()"
                      class="btn-base input-choice-btn input-btn"
                      :style="getStyles(input)">
                <div v-html="sessionInputLabel(input)"></div>
              </button>
            </b-col>
            <b-col v-if="showBackButton" cols="12" class="back">
              <b-button block @click="goBack()" class="btn-outline-success btn-back stroke" variant="outline-primary">Back</b-button>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row v-else class="justify-content-center boxes select-item-btn customer-verify mt-4 variant-container-btns">
        <b-col v-if="showBackButton" md="4" cols="6" class="back">
          <b-button block @click="goBack()" class="btn-outline-success btn-back stroke" variant="outline-primary">Back</b-button>
        </b-col>
        <b-col :md="showBackButton ? 4 : 8" :cols="showBackButton ? 6 : 12">
          <b-button block @click="done()" class="btn btn-default btn-account font-weight-normal select-item-continue-btn" variant="primary">Continue
            <font-awesome-icon :icon="['fas', 'caret-right']" class="footer_btn"></font-awesome-icon>
          </b-button>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import {getDefaultProductImageUrl, updateFontSizes} from '@/utils'
import {StepCategory, VariantType} from '@/constants'
import VariantSelector from './VariantSelector'

const StepTitle = () => import('./StepTitle')

export default {
  name: 'Variants',
  components: {StepTitle, VariantSelector},
  props: {
    session: Object,
    item: Object,
    newCampaignProduct: Object,
    subscriptionOnly: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    quantity: {
      type: Number,
      default: function() {
        return 1
      }
    }
  },
  data: function() {
    return {
      product: null,
      bundleQuantity: 1,
      variantsList: [],
      initialVariant: null,
      initialBundledVariants: [],
      currentSelectedVariants: [],
      variantSelectionComplete: false
    }
  },
  computed: {
    expectedQuantity: function() {
      return this.isBundledVariant ? this.quantity * this.bundleQuantity : this.quantity
    },
    showBackButton: function() {
      return this.session && (this.session.can_undo || this.session.is_step_preview)
    },
    getImageUrl: function() {
      return getDefaultProductImageUrl(this.product, 'small')
    },
    title: function() {
      return 'Choose Your Options'
    },
    subtitle: function() {
      // VariantSelector handles the quantity mode internally and shows its own summary
      return this.expectedQuantity > 1 ? 'Select options for ' + this.expectedQuantity.toString() + ' items' : ''
    },
    isBundledVariant: function() {
      return this.product && this.product.variant_type === VariantType.Bundled
    },
    showStepLabels: function() {
      return this.session && this.session.step_category === StepCategory.ChangeVariant
    }
  },
  methods: {
    onVariantSelectionChange: function({ isComplete, selectedVariants }) {
      this.variantSelectionComplete = isComplete
      this.currentSelectedVariants = selectedVariants
    },
    done: function() {
      this.$store.commit('clear_messages')

      // Validate using VariantSelector's state
      if (!this.variantSelectionComplete) {
        this.$store.commit('error', 'Please fill out all fields')
        return
      }

      // Transform variants to API format {id, quantity} instead of {variant, quantity}
      const variantsForAPI = this.currentSelectedVariants.map(v => ({
        id: v.variant.id,
        quantity: v.quantity
      }))

      // Emit selection in API format
      this.$emit('select', {variants: variantsForAPI})
    },
    goBack: function() {
      this.$emit('back') // go back to the previous step (ignore selections)
    },
    getStyles: function(input) {
      return input && input.button_styles && input.button_styles.style
    },
    sessionInputLabel(input) {
      if (input) {
        return updateFontSizes(input.formatted_label)
      }
      return null
    }
  },
  created() {
    this.$store.commit('busy')
    let product = this.item.product
    let selectedVariant = this.item.variant
    let bundleQuantity = this.item.campaign_product.bundle_quantity
    let bundledVariants = this.item.bundled_variants

    if (this.newCampaignProduct) {
      product = this.newCampaignProduct.product
      bundleQuantity = this.newCampaignProduct.bundle_quantity
      selectedVariant = null
      bundledVariants = []
    } else if (this.subscriptionOnly) {
      product = this.item.next_cycle_product
      bundleQuantity = this.item.next_bundle_quantity
      selectedVariant = this.item.next_variant
      bundledVariants = this.item.next_bundled_variants
    }

    axios.get('/products/' + product.id.toString() + '/variants/').then(response => {
      const variants = response.data
      this.product = product
      this.bundleQuantity = bundleQuantity || 1

      // Set up data for VariantSelector component
      this.variantsList = variants
      this.initialVariant = selectedVariant
      this.initialBundledVariants = bundledVariants || []

      this.$store.commit('ready')
    }, error => {
      console.error(error)
      this.$store.commit('critical_error', error)
      this.$store.commit('ready')
    })
  }
}
</script>

<style scoped lang="less">
.stroke{
  border: 2px solid;
}

.variant-btns-wrapper {
  .choice-fields-btns {
    margin: 0 auto;
  }

  button,
  .btn-back {
    padding: 12px 16px !important;
    min-height: 44px !important;
    text-transform: none;
  }
  .btn-back {
    padding: 8px 16px !important;
  }
}

</style>
